import React, { useEffect, useState } from 'react';
import { RuleExpression, RuleDescriptor, RefinedRule } from 'src/data/types/descriptor';
import AsyncSelect from 'react-select/async-creatable';
import { components, MenuListProps } from 'react-select';
import api from 'src/api';
import { ButtonTransparent, InputText, Select } from 'src/components/Layout/Forms';
import { PredefinedProp } from './definitions';
import { UpgradeTierBadge } from 'src/components/UpgradeTier';
import { isInTrial } from 'legacy/utils/flags';

export function EventRow(props: { expression: RuleExpression, onAttrSelected: (string) => void }) {
    const [serverDefaultOptions, setServerDefaultOptions] = useState<Array<{ label: string, value: string }> | undefined>(undefined);
    let finalOpts = serverDefaultOptions;
    const [_, selectedEvent] = props.expression.prop.split('.');
    const defaultOption = selectedEvent ? { label: selectedEvent, value: selectedEvent } : undefined;

    useEffect(() => {
        api.events.getAvailableEvents({}, {}).then((res) => {
            const newState = res.items.map(i => ({ label: i, value: i }));
            if (defaultOption && !newState.some(s => s.value == defaultOption.value)) {
                defaultOption && newState.push(defaultOption);
            }
            setServerDefaultOptions(newState);
        });
    }, []);

    if (!finalOpts && defaultOption) {
        finalOpts = [defaultOption];
    }

    return <div>
        <AsyncSelect
            defaultOptions={finalOpts}
            defaultValue={defaultOption}
            classNamePrefix='rselect'
            menuPlacement='top'
            loadOptions={async (q) => {
                const resp = await api.events.getAvailableEvents({ eventNameContains: q }, {});

                return resp.items.map(i => ({ label: i, value: i }));
            }}
            placeholder='Select or enter a name...'
            // @ts-ignore
            onChange={(e) => props.onAttrSelected(e.value)}
            formatCreateLabel={(input) => `Use "${input}"`}
            components={
                {
                    MenuList: MenuCreatableListEx
                }
            }
        />

        {isInTrial() && <UpgradeTierBadge />}
    </div>
}

function MenuCreatableListEx(props: MenuListProps<{ label: string, value: string }>) {
    return <components.MenuList {...props}>
        {props.children}

        <div className='rselect-creatable-note'>Or enter an event name</div>
    </components.MenuList>
}

export function EventRefine(props: { rule: RuleDescriptor, onRefinedRuleChanged: (rule: RefinedRule) => void, onAddRefine: () => void, onRemoveRefine: () => void }) {
    const isExpanded = !!props.rule.refinedRule;
    const refinedRule = props.rule.refinedRule || {} as RefinedRule;

    const pluralize = (amount: number, text: string) => {
        if (amount == 1) return text;

        return text + 's';
    };

    const availableUnits = [
        { label: 'day', value: 'day' },
        { label: 'hour', value: 'hrs' },
        { label: 'minute', value: 'min' }
    ]

    const period = [
        { label: 'ever', value: 'ever' },
        { label: 'within the last', value: 'wlast' }
    ]
    const selectedPeriod = !!refinedRule.periodUnit ? 'wlast' : 'ever';

    const updateRange = (p: string) => {
        if (p == 'ever') {
            props.onRefinedRuleChanged({
                ...refinedRule,
                periodUnit: null
            })
        } else {
            props.onRefinedRuleChanged({
                ...refinedRule,
                periodUnit: 'day',
                periodAmount: 1
            });
        }
    }

    const updatePeriodAmount = (a: string) => {
        props.onRefinedRuleChanged({ ...refinedRule, periodAmount: handleNumericString(a) });
    }

    const updateAtLeast = (atl: string) => {
        props.onRefinedRuleChanged({ ...refinedRule, atLeastTimes: handleNumericString(atl) });
    }
    const updatePeriodUnit = (u: string) => {
        props.onRefinedRuleChanged({ ...refinedRule, periodUnit: u as 'day' | 'min' });
    }

    return <div>
        {!isExpanded && <a href="#" className='std-link' onClick={() => props.onAddRefine()}>Refine</a>}

        {isExpanded && <div className='event-refined-panel'>
            <span>at least</span>
            <InputText className='at-least' value={handleControlledValue(refinedRule.atLeastTimes)} onChange={(e) => updateAtLeast(e.target.value)} />
            <span>{pluralize(refinedRule.atLeastTimes, 'time')}</span>
            <Select className='range' defaultValue={selectedPeriod} onChange={(e) => updateRange(e.target.value)}>
                {period.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </Select>

            {selectedPeriod != 'ever' && <>
                <InputText className='period-amount' value={handleControlledValue(refinedRule.periodAmount)} onChange={(e) => updatePeriodAmount(e.target.value)} />
                <Select className='period-unit' value={refinedRule.periodUnit || ''} onChange={(e) => updatePeriodUnit(e.target.value)}>
                    {availableUnits.map(i => <option key={i.value} value={i.value}>{pluralize(refinedRule.periodAmount, i.label)}</option>)}
                </Select>
            </>}

            <ButtonTransparent onClick={() => props.onRemoveRefine()}>Remove</ButtonTransparent>
        </div>}
    </div>
}

function handleControlledValue(num?: number) {
    if (isNaN(num) || num == null) {
        return '';
    }

    return num;
}

function handleNumericString(str: string) {
    const n = parseInt(str);
    if (isNaN(n)) return null;
    return n;
}